import { Button } from '@insight-health-ai/roseate/components';
import { DatePicker } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';

type DateRangePickerProps = Pick<RangePickerProps, 'value' | 'showTime'> & {
  onChange: (
    arg1: Parameters<Required<RangePickerProps>['onChange']>[0],
    arg2: Parameters<Required<RangePickerProps>['onChange']>[1],
    quickFilterLabel?: string
  ) => void;
};

export const getDateRangeQuickFilters = () =>
  [
    {
      label: 'Today',
      value: [dayjs().startOf('day'), dayjs().endOf('day')],
    },
    {
      label: 'Yesterday',
      value: [
        dayjs().subtract(1, 'day').startOf('day'),
        dayjs().subtract(1, 'day').endOf('day'),
      ],
    },
    {
      label: 'Next 7 Days',
      value: [dayjs().startOf('day'), dayjs().add(7, 'day').endOf('day')],
    },
    {
      label: 'Last 7 Days',
      value: [dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')],
    },
    {
      label: 'Last 30 Days',
      value: [dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day')],
    },
  ] as const;

/** Returns the date range [start, end] for a quick-filter label (Today, Next 7 Days, etc.), or undefined if not found. */
export const getDateRangeFromQuickFilterLabel = (
  label: string
): [dayjs.Dayjs, dayjs.Dayjs] | undefined => {
  const item = getDateRangeQuickFilters().find(
    (f) => (f.label || '').toLowerCase() === (label || '').toLowerCase()
  );
  return item ? [item.value[0], item.value[1]] : undefined;
};

export const DateRangePicker = ({
  value,
  onChange,
  showTime,
}: DateRangePickerProps) => {
  const triggerChange = (
    value: [dayjs.Dayjs | null, dayjs.Dayjs | null],
    dateString: [string, string],
    quickFilterLabel?: string
  ) => {
    onChange?.(
      [
        showTime ? value?.[0] : value?.[0]?.startOf('day') ?? null,
        showTime ? value?.[1] : value?.[1]?.endOf('day') ?? null,
      ],
      [dateString?.[0] ?? null, dateString?.[1] ?? null],
      quickFilterLabel
    );
  };

  return (
    <>
      <style>
        {`.ih-date-range-picker-popup .ant-picker-panel-layout {
            > div {
              display: flex;
              flex-direction: column-reverse;
            }
          }`}
      </style>
      <DatePicker.RangePicker
        showTime={
          showTime
            ? {
                showSecond: false,
                defaultOpenValue: [
                  dayjs().startOf('day'),
                  dayjs().endOf('day'),
                ],
              }
            : false
        }
        allowEmpty
        allowClear
        width={'fit-content'}
        value={value}
        classNames={{
          popup: {
            root: 'ih-date-range-picker-popup',
          },
        }}
        onChange={(value, dateString) => {
          triggerChange(
            value as [dayjs.Dayjs | null, dayjs.Dayjs | null],
            dateString as [string, string]
          );
        }}
        format={(value) => {
          if (!value) return '';

          const shortFormat = showTime ? 'MMM DD, h:mm A' : 'MMM DD';
          const longFormat = showTime ? 'MMM DD, YY h:mm A' : 'MMM DD, YY';

          return dayjs(value).isSame(dayjs(), 'year')
            ? dayjs(value).format(shortFormat)
            : dayjs(value).format(longFormat);
        }}
        renderExtraFooter={() => (
          <div className="flex gap-2 py-4 justify-between items-center">
            <div className="flex gap-2 flex-wrap">
              {getDateRangeQuickFilters().map((item) => {
                const selected =
                  value &&
                  dayjs(value[0]).isSame(
                    item.value[0],
                    showTime ? 'minute' : 'day'
                  ) &&
                  dayjs(value[1]).isSame(
                    item.value[1],
                    showTime ? 'minute' : 'day'
                  );
                return (
                  <Button
                    size="sm"
                    shape="round"
                    variant={selected ? 'solid' : 'outlined'}
                    appearance="primary"
                    key={item.label}
                    onClick={() =>
                      triggerChange(
                        [item.value[0], item.value[1]],
                        [
                          item.value[0]?.toISOString(),
                          item.value[1]?.toISOString(),
                        ],
                        item.label
                      )
                    }
                  >
                    {item.label}
                  </Button>
                );
              })}
            </div>
            <a
              className="text-xs text-gray-600 font-semibold"
              onClick={() => {
                onChange?.(null, ['', '']);
              }}
            >
              Clear
            </a>
          </div>
        )}
      />
    </>
  );
};
